/* Lammes homepage — buy form for homepage feature + tiles.
   Selects start on their label ("Flavor", "Size"). Add to cart needs every select
   chosen: clicking with any select untouched adds nothing, outlines the empty
   select(s) and shows "Please choose your ..." under the button. Products with no
   select (a single-variant product) add in one click.

   Adds use the same request Horizon's own product form makes, and announce
   themselves with Shopify's standard cart event (@shopify/events), which is what
   this version of Horizon listens to for the header count and the cart drawer.
   The old custom 'cart:update' event is only a fallback. When Shopify refuses an
   add, its message is shown under the button instead of a bare "Try again". */
(function () {
  if (customElements.get('lam-buy-form')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const themeRoutes = () => (window.Theme && window.Theme.routes) || {};
  const addUrl = () => themeRoutes().cart_add_url || root + 'cart/add.js';
  const cartJsonUrl = () => (themeRoutes().cart_url || root + 'cart') + '.js';

  let eventsModule;
  const loadEvents = () => {
    if (!eventsModule) {
      eventsModule = import('@shopify/events').catch((error) => {
        console.warn('[lam-buy-form] Standard cart events unavailable', error);
        return null;
      });
    }
    return eventsModule;
  };

  class LamBuyForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      const data = this.querySelector('[data-lam-variants]');
      if (!this.form || !data) return;

      this.variants = JSON.parse(data.textContent);
      this.idInput = this.form.querySelector('[data-lam-variant-id]');
      this.button = this.form.querySelector('button[type="submit"]');
      this.label = this.button.querySelector('[data-lam-label]');
      this.defaultLabel = this.button.dataset.label || this.label.textContent;
      this.selects = Array.from(this.form.querySelectorAll('select[data-lam-option]'));

      this.selects.forEach((select) => select.addEventListener('change', () => this.update()));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.update();
      loadEvents();
    }

    isComplete() {
      return this.selects.every((select) => select.value);
    }

    resolve() {
      const chosen = this.selects
        .filter((select) => select.value)
        .map((select) => [Number(select.dataset.lamOption), select.value]);
      const matches = (variant) => chosen.every(([index, value]) => variant.options[index] === value);
      return this.variants.find((v) => matches(v) && v.available) || this.variants.find(matches) || null;
    }

    update() {
      const complete = this.isComplete();
      const variant = complete ? this.resolve() : null;
      this.variant = variant;
      if (variant) this.idInput.value = variant.id;

      const available = Boolean(variant && variant.available);
      // While a choice is still missing the button stays clickable so it can prompt.
      this.button.disabled = complete && !available;
      this.label.textContent = !complete ? this.defaultLabel : !variant ? 'Unavailable' : available ? this.defaultLabel : 'Sold out';

      this.selects.forEach((select) => {
        const wrapper = select.closest('.lam-select');
        wrapper.classList.toggle('is-set', Boolean(select.value));
        wrapper.style.outline = '';
      });
      this.clearMessage();
    }

    promptSelection() {
      const missing = this.selects.filter((select) => !select.value);
      if (!missing.length) return;
      const names = missing.map((select) => {
        const first = select.options[0];
        return ((first && first.textContent) || select.getAttribute('aria-label') || 'option').trim().toLowerCase();
      });
      missing.forEach((select) => {
        select.closest('.lam-select').style.outline = '2px solid #ED1C2E';
      });
      this.showMessage('Please choose your ' + names.join(' and ') + '.');
      missing[0].focus();
    }

    async submit(event) {
      if (!window.fetch) return; // no-JS path posts the form to /cart/add
      event.preventDefault();
      if (this.busy) return;
      if (!this.isComplete()) {
        this.promptSelection();
        return;
      }
      if (!this.variant || !this.variant.available) return;

      this.busy = true;
      this.clearMessage();
      this.button.setAttribute('aria-busy', 'true');

      const variantId = String(this.variant.id);
      const events = await loadEvents();
      const Lines = events && events.CartLinesUpdateEvent;

      const formData = new FormData(this.form);
      formData.set('id', variantId);
      formData.set('quantity', '1');
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      let deferred = null;
      if (Lines) {
        deferred = Lines.createPromise();
        this.dispatchEvent(
          new Lines({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
      }

      try {
        const response = await fetch(addUrl(), {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: formData,
          credentials: 'same-origin',
        });
        const result = await response.json().catch(() => ({}));
        const failed = !response.ok || Boolean(result.status);

        const cart = await fetch(cartJsonUrl(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());

        if (deferred) {
          deferred.resolve({
            cart: Lines.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'lam-buy-form',
              sourceId: this.id || 'lam-buy-form',
              itemCount: 1,
              sections: failed ? undefined : result.sections,
              didError: failed,
            },
          });
        } else if (!failed) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: cart, sourceId: this.id || 'lam-buy-form', data: { source: 'lam-buy-form', itemCount: cart.item_count } },
            })
          );
        }

        if (failed) {
          if (events && events.CartErrorEvent) {
            this.dispatchEvent(
              new events.CartErrorEvent({
                error: result.message || 'Add to cart failed',
                code: 'INVALID',
                detail: { description: result.description, errors: result.errors },
              })
            );
          }
          throw new Error(result.description || result.message || 'Add to cart failed (' + response.status + ')');
        }

        this.flash('Added');
      } catch (error) {
        if (deferred) deferred.reject(error);
        console.error('[lam-buy-form]', error);
        this.showMessage(error && error.message ? error.message : 'Something went wrong. Please try again.');
        this.flash('Try again');
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    showMessage(text) {
      if (!this.message) {
        this.message = document.createElement('p');
        this.message.className = 'lam-buy__msg';
        this.message.setAttribute('role', 'alert');
        this.message.style.cssText = 'margin:12px 0 0;font-size:16px;line-height:1.3;color:#282828;';
        this.form.appendChild(this.message);
      }
      this.message.textContent = text;
      this.message.hidden = false;
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => this.clearMessage(), 8000);
    }

    clearMessage() {
      if (this.message) this.message.hidden = true;
    }

    flash(text) {
      this.label.textContent = text;
      this.button.classList.add('is-done');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.button.classList.remove('is-done');
        const message = this.message && !this.message.hidden ? this.message.textContent : null;
        this.update();
        if (message) this.showMessage(message);
      }, 1800);
    }
  }

  customElements.define('lam-buy-form', LamBuyForm);
})();
